// The scope vocabulary, in one place, because three consumers need to agree on
// it: the token endpoint that grants them, the /v1 routes that require them,
// and the docs site that lists them.

export const PARTNER_SCOPES = [
  'rentals:quote',
  'rentals:book',
  'transfers:quote',
  'transfers:book',
  'bookings:read',
  'bookings:cancel',
  'sdk:sessions',
  // Webhook endpoint management (P3 Task 6): register/list/update/delete a
  // partner's own webhook endpoints, rotate their secret, send a test ping,
  // inspect delivery history, and replay a delivery. Deliberately separate
  // from bookings:read/bookings:cancel — a partner integration that only
  // reads or cancels bookings has no business also being able to redirect
  // where booking-lifecycle events get POSTed.
  'webhooks:manage',
] as const;

export type PartnerScope = (typeof PARTNER_SCOPES)[number];

export function isPartnerScope(s: string): s is PartnerScope {
  return (PARTNER_SCOPES as readonly string[]).includes(s);
}

// ─── What a partner may grant ITSELF ────────────────────────────────────────
//
// DriveCars ops can put any scope on a credential (the backoffice lists all of
// PARTNER_SCOPES). A partner minting its own credential from the console may
// not: a scope is "this key is allowed to try", and a partner must never be
// able to hand itself a key for a product ops did not sell it. The /v1 product
// gate (requireProduct) would still refuse the call — but a `transfers:book`
// key in the hands of a rentals-only partner is one ops edit away from live,
// and nobody at DriveCars would have decided to issue it.
//
// So product scopes follow partners.enabled_products, and the rest — which act
// only on what the partner already owns — are always grantable.
//
// A scope with an UNKNOWN prefix is NOT grantable. That is deliberate: a new
// product's scopes added to PARTNER_SCOPES stay ops-only until someone adds
// the product here, rather than becoming self-service by default.

const SCOPE_PRODUCT: Partial<Record<PartnerScope, string>> = {
  'rentals:quote': 'rentals',
  'rentals:book': 'rentals',
  'transfers:quote': 'transfers',
  'transfers:book': 'transfers',
};

const ALWAYS_GRANTABLE: readonly PartnerScope[] = [
  'bookings:read',
  'bookings:cancel',
  'webhooks:manage',
  'sdk:sessions',
];

/** The scopes a partner holding `enabledProducts` may put on a credential it
 *  mints for itself, in PARTNER_SCOPES order. */
export function grantableScopes(enabledProducts: readonly string[]): PartnerScope[] {
  return PARTNER_SCOPES.filter((scope) => {
    if (ALWAYS_GRANTABLE.includes(scope)) return true;
    const product = SCOPE_PRODUCT[scope];
    return product !== undefined && enabledProducts.includes(product);
  });
}
